import { useCallback, useState } from 'react';
import { Button, styled } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import config from '../config.json';
import {
  disableOperationsHelpDesk,
  ErrorIcon,
  Error,
  IUser,
  SuccessIcon,
  toggleItem,
  useRoleOpContext,
  StyledTablePagination,
} from '../common';
import {
  IRequestApiCallStatus,
  StatusSaveRequst,
  TeamRequestAction,
} from '../team-request';
import { Justification, justificationOptions } from '../request';
import { useConfirmSubmitRequest } from '../admin';
import {
  ICert,
  UserRoles2d,
  certsAllSelected,
  excludeUserRoles,
  saveRequest,
  useLegacyCertSearch,
} from '.';
import { noop, filter as lfilter, map as lmap, isEmpty } from 'lodash/fp';
import { always, pathOr } from 'rambda';
import { map } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/lib/function';

const rowsPerPageOptions = [10, 25, 50, 100, 500];

export const Request = ({ user }: { user: IUser }) => {
  const [email, setEmail] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState<string | null>(null);
  const [deaNumber, setDeaNumber] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    certs,
    loading: loadingCerts,
    load,
  } = useLegacyCertSearch({
    email,
    serialNumber,
    deaNumber,
    page,
    pageSize,
  });

  const handleChangePage = flow((_: unknown, newPage: number) => {
    setPage(newPage);
  }, load);

  const handleChangeRowsPerPage = flow(
    pathOr('10', 'target.value'),
    (a: string) => parseInt(a, 10),
    setPageSize,
    always(0),
    setPage,
    load
  );

  const [apiCallStatus, setApiCallStatus] = useState<IRequestApiCallStatus>({
    type: 'ConfirmSubmitRequest',
    apiCallStatus: void 0,
  });

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const toggleUserRole = flow(
    ({ type, payload }: TeamRequestAction) =>
      type === 'ToggleUserRole'
        ? payload === 'all'
          ? toggleUserRoleAll()
          : pipe(payload, toggleItem(selectedKeys), setSelectedKeys)
        : void 0,
    always<IRequestApiCallStatus>({
      type: 'ConfirmSubmitRequest',
      apiCallStatus: void 0,
    }),
    setApiCallStatus
  );

  const toggleUserRoleAll = flow(
    () =>
      pipe(
        certsAllSelected({
          certs: excludeUserRoles(revokedUserRoleKeys)(certs.content),
          selectedKeys,
        }),
        allSelected =>
          allSelected
            ? setSelectedKeys([])
            : setSelectedKeys(
                pipe(
                  excludeUserRoles(revokedUserRoleKeys)(certs.content),
                  map<ICert, string>(pathOr('', 'key'))
                )
              )
      ),
    always<IRequestApiCallStatus>({
      type: 'ConfirmSubmitRequest',
      apiCallStatus: void 0,
    }),
    setApiCallStatus
  );

  const [justification, setJustification] = useState('');
  const [otherComments, setOtherComments] = useState('');

  const dispatchSaveRequest = useCallback(
    ({ type, payload }: TeamRequestAction) =>
      type === 'SaveRequestSuccess'
        ? flow(
            () =>
              setApiCallStatus({
                type: 'SaveRequest',
                apiCallStatus: true,
              }),
            () => setRevokedUserRoleKeys(a => [...a, ...selectedKeys]),
            () => setSelectedKeys([])
          )()
        : setApiCallStatus({
            type: 'SaveRequest',
            apiCallStatus: false,
          }),
    [selectedKeys]
  );

  const dispatchConfirmSubmitRequest = useCallback(
    ({ type, payload }: TeamRequestAction) =>
      type === 'SaveRequest'
        ? flow(
            () =>
              setApiCallStatus(a => ({
                ...a,
                type: 'SaveRequest',
                apiCallStatus: null,
              })),
            () =>
              saveRequest(dispatchSaveRequest)(user)({
                serialNumbers: flow(
                  lfilter<ICert>(({ key }) => selectedKeys.includes(key)),
                  lmap<ICert, string>(pathOr('', 'srNum'))
                )(certs.content),
                comments:
                  justification === 'Other' ? otherComments : justification,
              })
          )()
        : setApiCallStatus(a => ({ ...a, apiCallStatus: void 0 })),
    [
      justification,
      otherComments,
      selectedKeys,
      user,
      certs.content,
      dispatchSaveRequest,
    ]
  );

  useConfirmSubmitRequest({
    apiCallStatus,
    dispatchRequest: dispatchConfirmSubmitRequest,
  });

  const [revokedUserRoleKeys, setRevokedUserRoleKeys] = useState<string[]>([]);

  const { operations } = useRoleOpContext();
  const disableOperationsHelpDsk = disableOperationsHelpDesk(operations);

  return (
    <Wrapper>
      <article>
        <h3>Search User Certificates</h3>
        <FilterWrapper>
          <ActionLabel id="email">Email:</ActionLabel>
          <StyledInput
            aria-labelledby="email"
            value={email || ''}
            onChange={flow(
              pathOr(null, 'target.value'),
              setEmail,
              () => setDeaNumber(null),
              () => setSerialNumber(null),
              always(0),
              setPage,
              always<IRequestApiCallStatus>({
                type: 'ConfirmSubmitRequest',
                apiCallStatus: void 0,
              }),
              setApiCallStatus
            )}
            type="text"
          />

          <FilterLabel id="deaNum">DEA Registration Number:</FilterLabel>
          <StyledInput
            aria-labelledby="deaNum"
            value={deaNumber || ''}
            onChange={flow(
              pathOr(null, 'target.value'),
              setDeaNumber,
              () => setEmail(null),
              () => setSerialNumber(null),
              always(0),
              setPage,
              always<IRequestApiCallStatus>({
                type: 'ConfirmSubmitRequest',
                apiCallStatus: void 0,
              }),
              setApiCallStatus
            )}
            type="text"
          />

          <FilterLabel id="serialNumber">
            Certificate Serial Number:
          </FilterLabel>

          <StyledInput
            aria-labelledby="serialNumber"
            value={serialNumber || ''}
            onChange={flow(
              pathOr(null, 'target.value'),
              setSerialNumber,
              () => setEmail(null),
              () => setDeaNumber(null),
              always(0),
              setPage,
              always<IRequestApiCallStatus>({
                type: 'ConfirmSubmitRequest',
                apiCallStatus: void 0,
              }),
              setApiCallStatus
            )}
            type="text"
          />
          <span></span>
          <Button
            onClick={flow(
              load,
              always<IRequestApiCallStatus>({
                type: 'ConfirmSubmitRequest',
                apiCallStatus: void 0,
              }),
              setApiCallStatus
            )}
            disabled={loadingCerts === null}
            variant="contained"
            color="primary">
            search certificates
          </Button>
        </FilterWrapper>
      </article>
      <SectionWrapper>
        <h3>Revoke List</h3>
        {loadingCerts === false ? (
          <span>
            <ErrorIcon />
            <Error>Failed to search certificates</Error>
          </span>
        ) : loadingCerts === null ? (
          <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'right',
                alignItems: 'center',
              }}>
              {excludeUserRoles(revokedUserRoleKeys)(certs.content).length !==
                pageSize &&
                page < Math.ceil(certs.total / pageSize) - 1 && (
                  <h4>{config.legacyCertHint}</h4>
                )}
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={certs.total}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
            <UserRoles2d
              certs={excludeUserRoles(revokedUserRoleKeys)(certs.content)}
              selectedKeys={selectedKeys}
              enableAll
              dispatchTeamRequest={toggleUserRole}
            />
          </>
        )}
      </SectionWrapper>
      <Actions>
        <Justification
          value={justification}
          options={justificationOptions.REVOKE}
          onChange={setJustification}
          otherComments={otherComments}
          onChangeOtherComments={setOtherComments}
          tooltipValue={always(false)}
          toggleTooltip={noop}
          offTooltip={noop}
        />
      </Actions>

      <StatusSaveRequst apiCallStatus={apiCallStatus.apiCallStatus} />
      {apiCallStatus.apiCallStatus === true && (
        <article>
          <SuccessIcon />
          <span> {config.requestSubmitSuccess} </span>
        </article>
      )}

      <Button
        onClick={() =>
          setApiCallStatus(a => ({
            ...a,
            type: 'ConfirmSubmitRequest',
            apiCallStatus: null,
          }))
        }
        disabled={
          disableOperationsHelpDsk ||
          isEmpty(selectedKeys) ||
          isEmpty(justification) ||
          apiCallStatus.apiCallStatus === null
        }
        variant="contained"
        color="primary">
        submit
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '3rem',
  },
}));

const FilterWrapper = styled('span')(({ theme }) => ({
  margin: '1.5rem',
  display: 'grid',
  gap: '0.5rem',

  '@media (min-width: 33.375em)': {
    display: 'grid',
    gridTemplateColumns: '200px  300px 200px 300px ',
    gap: '0.5rem',
    alignItems: 'center',
  },
}));

const SectionWrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const ActionLabel = styled('span')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
  },
}));

const FilterLabel = styled('span')(({ theme }) => ({
  marginTop: '0.5rem',

  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
    marginLeft: '5rem',
    marginTop: '0rem',
  },
}));

const Actions = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: '0.5rem',
  alignItems: 'center',
  gridAutoRows: 'minmax(1rem,auto)',

  '@media (min-width: 33.375em)': {
    gridTemplateColumns: 'auto 200px auto auto auto 1fr',
  },
}));

const StyledInput = styled('input')({
  padding: '0.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  '&:focus': {
    outline: 'none',
    borderColor: '#1976d2',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.25)',
  },
});
